import * as Dialog from '@radix-ui/react-dialog';
import React, { type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { tv } from '@/utils/tv';
import { Close } from '@/components/Icons/Close';
import { PageSpinner } from '@/components/Spinner/PageSpinner';
import { useCloseOnBack } from '@/hooks/useCloseOnBack';
import { ModalSize, ModalVariant, SpinnerColor } from '@/types/components';

const overlay = tv({
  base: 'fixed inset-0 bg-black/40',
  variants: {
    variant: {
      [ModalVariant.Default]: 'z-40 hidden md:block',
      [ModalVariant.BottomSheet]: 'z-[70]',
    },
  },
});

const spinner = tv({
  base: 'size-[22px]',
  variants: {
    color: {
      [SpinnerColor.Grey]: 'text-subtle-foreground',
      [SpinnerColor.Blue]: 'text-primary',
    },
  },
});

const content = tv({
  base: 'fixed flex flex-col bg-background outline-none md:inset-auto md:top-1/2 md:left-1/2 md:-translate-x-1/2 md:-translate-y-1/2 md:overflow-hidden md:rounded-xl md:border md:border-border md:shadow-modal',
  variants: {
    size: {
      [ModalSize.Small]: 'md:h-[440px] md:w-[400px]',
      [ModalSize.Medium]: 'md:h-[620px] md:w-[400px]',
      [ModalSize.Large]:
        'md:h-[788px] md:w-[400px] md:max-h-[calc(100svh-80px)]',
      [ModalSize.XLarge]:
        'md:h-[620px] md:w-[min(950px,calc(100vw-80px))] md:max-h-[calc(100svh-80px)]',
    },
    variant: {
      [ModalVariant.Default]:
        'z-50 inset-0 max-md:data-[state=open]:animate-zoom-in md:data-[state=open]:animate-sheet-slide-up',
      [ModalVariant.BottomSheet]:
        'z-[70] inset-x-0 bottom-0 max-h-[75svh] overflow-hidden rounded-t-3xl data-[state=open]:animate-sheet-slide-up max-md:data-[state=closed]:animate-sheet-slide-down',
    },
  },
});

interface Props {
  isOpen: boolean;
  isHiddenOnDesktop?: boolean;
  label: string;
  children: ReactNode;
  size?: ModalSize;
  variant?: ModalVariant;
  className?: string;
  isLoading?: boolean;
  spinnerColor?: SpinnerColor;
  contentRef?: React.Ref<HTMLDivElement>;
  onClickOutside?: () => boolean;
  onClose: () => void;
}

export const Modal: React.FC<Props> = ({
  isOpen,
  isHiddenOnDesktop = false,
  label,
  children,
  size,
  variant = ModalVariant.Default,
  className,
  isLoading,
  spinnerColor = SpinnerColor.Grey,
  contentRef,
  onClickOutside,
  onClose,
}) => {
  const { t } = useTranslation();
  const hiddenClassName = isHiddenOnDesktop ? 'md:invisible' : '';

  const handleOpenChange = (isNextOpen: boolean) => {
    if (isNextOpen) {
      return;
    }
    if (onClickOutside?.()) {
      return;
    }
    onClose();
  };

  useCloseOnBack(isOpen, () => handleOpenChange(false));

  return (
    <Dialog.Root open={isOpen} onOpenChange={handleOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay
          className={overlay({ variant, className: hiddenClassName })}
        />
        <Dialog.Content
          ref={contentRef}
          aria-describedby={undefined}
          className={content({
            size,
            variant,
            className: [className, hiddenClassName],
          })}
        >
          <Dialog.Title className="sr-only">{label}</Dialog.Title>
          {isLoading && (
            <div className="flex h-full flex-col">
              <div className="flex shrink-0 items-center justify-between bg-background px-4 pt-[calc(16px+env(safe-area-inset-top))] pb-3 md:px-5 md:pt-5 md:pb-4">
                <h2 className="text-callout-16 truncate text-foreground md:text-headline-20">
                  {label}
                </h2>
                <button
                  type="button"
                  aria-label={t('global.close')}
                  onClick={onClose}
                  className="flex size-6 shrink-0 cursor-pointer items-center justify-center text-foreground"
                >
                  <Close />
                </button>
              </div>
              <div className="flex flex-1 items-center justify-center">
                <PageSpinner className={spinner({ color: spinnerColor })} />
              </div>
            </div>
          )}
          {!isLoading && children}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};
